import { useCallback, useEffect, useMemo, useState } from 'react';
import { deckService, flashcardService } from '../../queries';
import MemoryButton from '../memory-button';
import { Modal } from '../modal';
import { useUserStore } from '../../../Auth/stores/authStore';
import { toast } from '../../../../core/components/atoms/Toast';
import { Typography } from '../../../../core/components/molecules/Typography';
import useDecks from '../../hooks/use-decks';
import { Icon } from '../../../../core/components/atoms/Icon';
import MultiSelect from '../multi-select';
import useFlashcardDiscipline from '../../hooks/use-flashcard-discipline';
import useTags, { TAG_TYPE } from '../../hooks/use-tags';
import { useParams } from 'react-router-dom';
import useSubjects from '../../hooks/use-subjects';
import BrainLoadingWrapper from '../../../../core/components/molecules/BrainLoading';

const ModalCreateFlashcard = ({
  isDiscipline,
  onCallbackSuccess,
  btnCreateLabel,
  editFlashcardUuid,
  open,
  onClose,
  defaultDeckUuid,
}: {
  open?: boolean;
  isDiscipline?: boolean;
  onCallbackSuccess: () => void;
  btnCreateLabel?: string | React.ReactNode;
  editFlashcardUuid?: string;
  onClose?: () => void;
  defaultDeckUuid?: string;
  defaultDiscipline?: string;
}) => {
  const { user } = useUserStore();
  const userUuid = user?.memoryUserUuid;
  const { disciplineUuid } = useParams();

  const { flashcards } = useFlashcardDiscipline();

  const { decks } = useDecks();
  const { tags: allTags } = useTags();
  const disciplineTagsResponse = useTags(TAG_TYPE.DISCIPLINA);

  const disciplineTags = (disciplineTagsResponse?.tags || [])?.filter(
    (el) => el.slug
  );

  const [isOpen, setIsOpen] = useState(open || false);
  const [flashcard, setFlashcard] = useState<FlashcardEditProps>({
    front: '',
    back: '',
    deckUuids: [],
    tagsUuids: [],
    discipline: undefined,
    flashcardUuid: '',
  } as FlashcardEditProps);
  const [errors, setErrors] = useState<{
    front?: string;
    back?: string;
    deckUuids?: string;
    discipline?: string;
  }>({});
  const [isLoadingFlashcard, setIsLoadingFlashcard] = useState(false);

  const { subjects } = useSubjects();

  const shouldDisabledSubjects = !flashcard?.discipline?.uuid;

  const isSaveDisabled = useMemo(() => {
    const baseValidation =
      !flashcard.front || !flashcard.back || !flashcard.discipline?.uuid;
    if (isDiscipline) {
      return baseValidation;
    }
    return baseValidation || !flashcard.deckUuids.length;
  }, [flashcard, isDiscipline]);

  const validateFields = () => {
    const newErrors: typeof errors = {};

    if (!flashcard.front) {
      newErrors.front = 'Campo obrigatório';
    }
    if (!flashcard.back) {
      newErrors.back = 'Campo obrigatório';
    }
    if (!isDiscipline && !flashcard.deckUuids.length) {
      newErrors.deckUuids = 'Selecione pelo menos um deck';
    }
    if (!flashcard.discipline?.uuid) {
      newErrors.discipline = 'Selecione uma disciplina';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveFlashcard = async () => {
    if (!userUuid) return;
    if (!validateFields()) return;

    const isEditing = !!editFlashcardUuid;
    const action = isEditing ? 'atualizar' : 'criar';

    const payload = {
      userUuid,
      front: flashcard.front,
      back: flashcard.back,
      deckUuids: flashcard.deckUuids || [],
      tagsUuids: flashcard.tagsUuids || [],
      isDiscipline,
      disciplineUuid: flashcard.discipline?.uuid,
      ...(isEditing && { flashcardUuid: editFlashcardUuid }),
      ...(!isEditing && { typeName: 'USER' }),
    };

    try {
      const serviceFn = isEditing
        ? deckService.updateFlashcard
        : deckService.createFlashcard;

      await serviceFn(payload as any);

      toast({
        title: `Flashcard ${action} com sucesso`,
        variant: 'success',
      });

      onCallbackSuccess?.();
      handleClose();
    } catch (error) {
      console.error(error);
      toast({
        title: `Erro ao ${action} flashcard`,
        message: 'Tente novamente mais tarde',
        variant: 'error',
      });
    }
  };

  const handleAddDeck = (deckUuid: string) => {
    const flashcardDeckUuids = flashcard?.deckUuids || [];
    const deckAlreadyExists = flashcardDeckUuids.includes(deckUuid);
    setFlashcard({
      ...flashcard,
      deckUuids: deckAlreadyExists
        ? flashcardDeckUuids.filter((uuid) => uuid !== deckUuid)
        : [...flashcardDeckUuids, deckUuid],
    });
  };

  const handleRemoveDeck = (deckUuid: string) => {
    setFlashcard({
      ...flashcard,
      deckUuids: flashcard.deckUuids.filter((uuid) => uuid !== deckUuid),
    });
  };

  const decksOptions = useMemo(() => {
    return decks?.filter(
      (deck) => !(flashcard?.deckUuids || []).includes(deck.uuid)
    );
  }, [decks, flashcard?.deckUuids, flashcards]);

  const tagsOptions = useMemo(() => {
    const flashcardDisciplineUuid = flashcard?.discipline?.uuid;
    const filteredSubjects = (subjects || [])
      ?.filter(
        (tag) =>
          tag.parentTagUuid === flashcardDisciplineUuid ||
          !flashcardDisciplineUuid
      )
      .map((tag) => ({
        uuid: tag.uuid,
        title: tag.name,
      }));

    return filteredSubjects;
  }, [subjects, flashcard?.discipline]);

  const handleClose = () => {
    setFlashcard({} as FlashcardEditProps);
    setErrors({});
    onClose?.();
    setIsOpen(false);
  };

  const getDeckTitle = useCallback(
    (deckUuid: string) => {
      return decks?.find((deck) => deck.uuid === deckUuid)?.title;
    },
    [decks]
  );
  const getTagTitle = useCallback(
    (tagUuid: string) => {
      return allTags?.find((tag) => tag.uuid === tagUuid)?.name;
    },
    [allTags]
  );

  const handleAddTag = (tagUuid: string) => {
    const tagAlreadyExists = flashcard.tagsUuids?.some(
      (tag) => tag === tagUuid
    );

    setFlashcard({
      ...flashcard,
      tagsUuids: tagAlreadyExists
        ? flashcard.tagsUuids?.filter((tag) => tag !== tagUuid)
        : [...(flashcard.tagsUuids || []), tagUuid],
    });
  };

  const handleAddDiscipline = (discipline: Tag) => {
    setFlashcard({
      ...flashcard,
      discipline,
      tagsUuids: [],
    });
  };

  const handleRemoveTag = (tagUuid: string) => {
    setFlashcard((prev) => {
      return {
        ...prev,
        tagsUuids: prev.tagsUuids?.filter((tag) => tag !== tagUuid),
      };
    });
  };

  const fetchFlashcardData = useCallback(async () => {
    if (!userUuid || !editFlashcardUuid) return;

    setIsLoadingFlashcard(true);
    try {
      const data = await flashcardService.get(userUuid, editFlashcardUuid);
      setFlashcard({
        flashcardUuid: data.uuid,
        front: data.front,
        back: data.back,
        deckUuids: data.decksVinculed || [],
        tagsUuids: (data.tags || []).map((tag) => tag.uuid),
        discipline: data.discipline,
      });
    } catch (error) {
      console.error('Erro ao carregar flashcard:', error);
      toast({
        title: 'Erro ao carregar flashcard',
        message: 'Tente novamente mais tarde',
        variant: 'error',
      });
    } finally {
      setIsLoadingFlashcard(false);
    }
  }, [userUuid, editFlashcardUuid]);

  useEffect(() => {
    if (!open) return;

    setIsOpen(true);

    if (editFlashcardUuid) fetchFlashcardData();

    const initialDeckUuids =
      typeof defaultDeckUuid === 'string' ? [defaultDeckUuid] : [];

    setFlashcard((prev) => ({
      ...prev,
      deckUuids: initialDeckUuids,
    }));
  }, [open, defaultDeckUuid, editFlashcardUuid, fetchFlashcardData]);

  useEffect(() => {
    if (!isOpen || editFlashcardUuid) return;

    const discipline = disciplineTags?.find(
      (tag) => tag.uuid === disciplineUuid
    );

    const initialDeckUuids =
      typeof defaultDeckUuid === 'string' ? [defaultDeckUuid] : [];

    setFlashcard((prev) => ({
      ...prev,
      deckUuids: initialDeckUuids,
      ...(discipline && {
        discipline,
      }),
    }));
  }, [
    isOpen,
    isDiscipline,
    disciplineUuid,
    editFlashcardUuid,
    defaultDeckUuid,
  ]);

  return (
    <>
      <MemoryButton
        label={
          btnCreateLabel || (
            <Typography className='dark:text-[#FFF]'>
              Criar <i>Flashcard</i>
            </Typography>
          )
        }
        variant='outline'
        onClick={() => setIsOpen(true)}
        className='w-full'
      />
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={
          <Typography className='text-mono-color-darkest'>
            {editFlashcardUuid ? 'Editar' : 'Criar'} <i>Flashcard</i>
          </Typography>
        }
      >
        {isLoadingFlashcard ? (
          <div className='flex h-[200px] items-center justify-center'>
            <BrainLoadingWrapper customMessage='Carregando...' />
          </div>
        ) : (
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-2'>
              <label
                htmlFor='deck-name'
                className='text-h4 text-mono-color-darkest'
              >
                <Typography className='text-h4 text-mono-color-darkest'>
                  Escolha o deck:
                  {isDiscipline ? '' : <span className='text-red-500'>*</span>}
                </Typography>
              </label>

              <MultiSelect
                options={decksOptions}
                handleAddOption={handleAddDeck}
                placeholder='Selecione o deck'
              />
              {errors.deckUuids && (
                <Typography className='text-red-500 text-sm'>
                  {errors.deckUuids}
                </Typography>
              )}
              <div className='flex flex-row gap-2'>
                <Typography className='text-sm text-mono-color-darkest '>
                  Você pode adicionar vários decks para adicionar seu cartão.
                </Typography>
              </div>
              <div className='flex flex-row flex-wrap gap-2'>
                {flashcard.deckUuids?.map((deckUuid) => (
                  <div
                    key={deckUuid}
                    className='flex flex-row items-center gap-2 rounded-sm bg-color-brand-primary px-2 py-1 text-xs text-card-color-default'
                  >
                    <div>{getDeckTitle(deckUuid)}</div>
                    <Icon
                      Icon='close'
                      type='material'
                      className='cursor-pointer text-sm text-card-color-default'
                      onClick={() => handleRemoveDeck(deckUuid)}
                    />
                  </div>
                ))}
              </div>
            </div>
            <hr className='mt-0 border-mono-color-light' />
            <div className='flex flex-col gap-2'>
              <label
                htmlFor='deck-name'
                className='text-h4 text-mono-color-darkest'
              >
                <Typography className='text-h4 text-mono-color-darkest'>
                  Frente <span className='text-red-500'>*</span>
                </Typography>
              </label>
              <input
                id='deck-name'
                className={`border flex items-center overflow-hidden rounded-md ${errors.front ? 'border-red-500' : 'border-mono-color-light'}`}
                value={flashcard.front}
                onChange={(e) =>
                  setFlashcard({ ...flashcard, front: e.target.value })
                }
                placeholder='Digite a frente do flashcard...'
              />
              {errors.front && (
                <Typography className='text-red-500 text-sm'>
                  {errors.front}
                </Typography>
              )}
            </div>
            <div className='flex flex-col gap-2'>
              <label
                htmlFor='deck-name'
                className='text-h4 text-mono-color-darkest'
              >
                <Typography className='text-h4 text-mono-color-darkest'>
                  Verso <span className='text-red-500'>*</span>
                </Typography>
              </label>
              <input
                id='deck-name'
                className={`border flex items-center overflow-hidden rounded-md ${errors.back ? 'border-red-500' : 'border-mono-color-light'}`}
                value={flashcard.back}
                onChange={(e) =>
                  setFlashcard({ ...flashcard, back: e.target.value })
                }
                placeholder='Digite o verso do flashcard...'
              />
              {errors.back && (
                <Typography className='text-red-500 text-sm'>
                  {errors.back}
                </Typography>
              )}
            </div>

            {!isDiscipline && (
              <div className='flex flex-col gap-2'>
                <label
                  htmlFor='discipline-name'
                  className='text-h4 text-mono-color-darkest'
                >
                  <Typography className='text-h4 text-mono-color-darkest'>
                    Escolha a disciplina:{' '}
                    <span className='text-red-500'>*</span>
                  </Typography>
                </label>

                <MultiSelect
                  options={disciplineTags?.map((el) => ({
                    uuid: el.uuid,
                    title: el.name,
                  }))}
                  handleAddOption={(disciplineUuid) => {
                    const discipline = disciplineTags?.find(
                      (tag) => tag.uuid === disciplineUuid
                    );
                    discipline && handleAddDiscipline(discipline as Tag);
                  }}
                  placeholder='Selecione a disciplina'
                />
                {errors.discipline && (
                  <Typography className='text-red-500 text-sm'>
                    {errors.discipline}
                  </Typography>
                )}
                <div className='flex flex-row gap-2'>
                  <Typography className='text-sm text-mono-color-darkest '>
                    Você pode adicionar uma disciplina para adicionar seu
                    cartão.
                  </Typography>
                </div>
                <div className='flex flex-row flex-wrap gap-2'>
                  {flashcard.discipline && (
                    <div
                      key={flashcard.discipline.uuid}
                      className='flex flex-row items-center gap-2 rounded-sm bg-color-brand-primary px-2 py-1 text-xs text-card-color-default'
                    >
                      <div>{getTagTitle(flashcard.discipline.uuid)}</div>
                      <Icon
                        Icon='close'
                        type='material'
                        className='cursor-pointer text-sm text-card-color-default'
                        onClick={() =>
                          setFlashcard({
                            ...flashcard,
                            discipline: undefined,
                          })
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className='flex flex-col gap-2'>
              <label
                htmlFor='subject-name'
                className='text-h4 text-mono-color-darkest'
              >
                <Typography className='text-h4 text-mono-color-darkest'>
                  Escolha os assuntos: <span className='text-red-500'>*</span>
                </Typography>
              </label>

              <MultiSelect
                options={tagsOptions}
                handleAddOption={handleAddTag}
                placeholder='Selecione os assuntos'
                disabled={shouldDisabledSubjects}
              />
              <div className='flex flex-row flex-wrap gap-2'>
                {flashcard.tagsUuids?.map((tagUuid) => (
                  <div
                    key={tagUuid}
                    className='flex flex-row items-center gap-2 rounded-sm bg-color-brand-primary px-2 py-1 text-xs text-card-color-default'
                  >
                    <div>{getTagTitle(tagUuid)}</div>
                    <Icon
                      Icon='close'
                      type='material'
                      className='cursor-pointer text-sm text-card-color-default'
                      onClick={() => handleRemoveTag(tagUuid)}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className='flex flex-row gap-2'>
              <MemoryButton
                label='Cancelar'
                variant='outline'
                className='w-full'
                onClick={handleClose}
                allowReadOnly
              />
              <MemoryButton
                label='Salvar'
                variant='primary'
                className='w-full bg-color-brand-primary disabled:bg-mono-color-medium'
                onClick={handleSaveFlashcard}
                disabled={isSaveDisabled}
              />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ModalCreateFlashcard;
